// src/features/pdf/pdf-merger/PdfMerger.tsx
import React, { useState, useCallback } from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import { Accept } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import PDFToolWorkspace from '../components/PDFToolWorkspace';
import ToolActionArea, { ToolStatus, OutputFile } from '../components/ToolActionArea';
import PdfViewerModal, { ViewerFile } from '../components/PdfViewerModal'; // Import the new modal
import { mergePdfs, downloadFile } from '../utils/pdfUtils';
import StyledFAQ from "../components/StyledFAQ";

// Define FileWithId interface
interface FileWithId {
    id: string;
    file: File;
}

const PdfMerger: React.FC = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [mergedPdf, setMergedPdf] = useState<Uint8Array | null>(null);
    const [error, setError] = useState<string>('');

    // State for the PDF viewer
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);

    // Handler to open the PDF viewer
    const handleOpenFileViewer = (file: ViewerFile) => {
        // Log opening file for debugging
        console.log('Opening file in viewer:', file.name);
        setViewerFile(file);
    };

    // Handler to close the PDF viewer
    const handleCloseFileViewer = () => {
        // Log closing viewer
        console.log('Closing file viewer.');
        setViewerFile(null);
    };

    // Reset state handler, but keep files for re-processing
    const resetProcessingState = useCallback(() => {
        setStatus('idle');
        setMergedPdf(null);
        setError('');
    }, []);

    // Handler for new files from Dropzone
    const handleDrop = useCallback((acceptedFiles: File[]) => {
        const newFiles = acceptedFiles.map(file => ({
            id: uuidv4(),
            file,
        }));
        setFiles(currentFiles => [...currentFiles, ...newFiles]);
        resetProcessingState(); // Reset status, but keep files
    }, [resetProcessingState]);

    const handleReorder = useCallback((reorderedFiles: FileWithId[]) => {
        setFiles(reorderedFiles);
    }, []);

    const handleDelete = useCallback((fileId: string) => {
        setFiles(currentFiles => currentFiles.filter(f => f.id !== fileId));
    }, []);

    const handleMerge = async () => {
        if (files.length < 2) {
            setError(t('pdf_merge_error_min_files', { ns: 'tools' }));
            setStatus('error');
            return;
        }
        setStatus('loading');
        setError('');
        try {
            const fileArray = files.map(f => f.file);
            const result = await mergePdfs(fileArray);
            setMergedPdf(result);
            setStatus('success');
        } catch (e) {
            console.error(e);
            setError(t('pdf_merge_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    };

    // Full reset handler for the "Reset" button
    const handleReset = useCallback(() => {
        setFiles([]);
        resetProcessingState();
    }, [resetProcessingState]);

    const handleDownload = () => {
        if (mergedPdf) {
            downloadFile(mergedPdf, 'laia-tools-merged.pdf');
        }
    };

    // Handler to preview the merged PDF
    const handlePreviewMerged = () => {
        if (mergedPdf) {
            handleOpenFileViewer({ name: 'laia-tools-merged.pdf', data: mergedPdf });
        }
    };

    // Create an OutputFile array for ToolActionArea
    const outputFiles: OutputFile[] = mergedPdf ? [{ name: 'laia-tools-merged.pdf', data: mergedPdf, size: mergedPdf.length }] : [];

    const faqs = [
        { q: 'pdf_merge_faq1_q', a: 'pdf_merge_faq1_a' },
        { q: 'pdf_merge_faq2_q', a: 'pdf_merge_faq2_a' },
        { q: 'pdf_merge_faq3_q', a: 'pdf_merge_faq3_a' },
    ];

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };

    return (
        <>
            <PdfViewerModal
                open={viewerFile !== null}
                onClose={handleCloseFileViewer}
                file={viewerFile}
            />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_merge"
                        descKey="pdf_merge_desc"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={true}
                        allowReordering={true}
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDelete}
                        onReorder={handleReorder}
                        onFileClick={handleOpenFileViewer} // Pass the handler
                    />

                    <ToolActionArea
                        status={status}
                        onAction={handleMerge}
                        onReset={handleReset}
                        onDownloadAll={mergedPdf ? handleDownload : undefined}
                        onPreviewOutput={mergedPdf ? handlePreviewMerged : undefined} // Pass preview handler
                        actionButtonText={t('pdf_merge_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_merge_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_merge_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFiles} // Pass output file info
                        disabled={files.length < 2 || status === 'loading'}
                    />
                </Stack>

                <Box sx={{ my: 15 }}>
                    <Typography sx={{ mb: 4 }} variant="h4" component="h2" gutterBottom textAlign="center">
                        {t('how_to_merge_pdf_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 10 }} color="text.secondary" textAlign="center">
                        {t('how_to_merge_pdf_desc', { ns: 'tools' })}
                    </Typography>

                    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 4 }}>
                        {t('what_is_pdf_merge_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mt: 4 }} paragraph>
                        {t('what_is_pdf_merge_p1', { ns: 'tools' })}
                    </Typography>

                    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 10 }}>
                        {t('why_use_our_tool_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mt: 4, mb: 15 }} paragraph>
                        {t('why_use_our_tool_p1', { ns: 'tools' })}
                    </Typography>

                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfMerger;